import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Link,
  InputAdornment,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
  Tooltip,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Cloud, Storage } from '@mui/icons-material';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, isEmulatorMode, toggleEmulatorMode } from '../../firebase';

interface LoginFormProps {
  onSignUp: () => void;
  onForgotPassword: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSignUp, onForgotPassword, setLoading, setError }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const isValidInput = email.includes('@') && password.length >= 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // If successful, the app will update based on auth state listener
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof Error) {
        if (error.message.includes('user-not-found')) {
          setError('No account exists with this email');
        } else if (error.message.includes('wrong-password')) {
          setError('Incorrect password');
        } else if (error.message.includes('too-many-requests')) {
          setError('Too many failed attempts. Try again later or reset your password');
        } else {
          setError(`Login failed: ${error.message}`);
        }
      } else {
        setError('An unknown error occurred during login');
      }
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Only show emulator toggle in development mode
  const showEmulatorToggle = process.env.NODE_ENV === 'development';

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={email !== '' && !email.includes('@')}
        helperText={email !== '' && !email.includes('@') ? 'Enter a valid email' : ''}
      />
      
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={password !== '' && password.length < 6}
        helperText={password !== '' && password.length < 6 ? 'Password must be at least 6 characters' : ''}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleTogglePasswordVisibility}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2, py: 1.5 }}
        disabled={!isValidInput}
      >
        Sign In
      </Button>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Link 
          component="button" 
          variant="body2" 
          onClick={onForgotPassword}
          sx={{ cursor: 'pointer' }}
        >
          Forgot password?
        </Link>
        
        <Link 
          component="button" 
          variant="body2" 
          onClick={onSignUp} 
          sx={{ cursor: 'pointer' }}
        >
          {"Don't have an account? Sign Up"}
        </Link>
      </Box>

      {/* Emulator Toggle - Only visible in development mode */}
      {showEmulatorToggle && (
        <>
          <Divider sx={{ mt: 3, mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title="Toggle between Firebase production and local emulator">
              <FormControlLabel
                control={
                  <Switch
                    checked={isEmulatorMode()}
                    onChange={toggleEmulatorMode}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isEmulatorMode() ? (
                      <>
                        <Storage color="warning" sx={{ mr: 0.5 }} fontSize="small" />
                        <Typography variant="body2" color="warning.main">
                          Using Firebase Emulator
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Cloud color="primary" sx={{ mr: 0.5 }} fontSize="small" />
                        <Typography variant="body2" color="primary">
                          Using Firebase Production
                        </Typography>
                      </>
                    )}
                  </Box>
                }
              />
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LoginForm; 